import { useState, useEffect, useCallback, useRef } from 'react';
import {
    ArrowLeft, Upload, Plus, Search, Filter, Loader2, ClipboardList, X, Save, Lock,
    CheckSquare, Layers, RefreshCw, Maximize2, Minimize2
} from 'lucide-react';
import { useAuth } from '@/context/useAuth';
import { apiGet, interactiveFetch } from '../api';
import WorklistKPIBar from '../components/worklist/WorklistKPIBar';
import WorklistRow from '../components/worklist/WorklistRow';
import PullMenu from '../components/worklist/PullMenu';
import WorklistDrawer from '../components/worklist/WorklistDrawer';
import WorklistFilterPanel from '../components/worklist/WorklistFilterPanel';
import WorklistPagination from '../components/worklist/WorklistPagination';
import SyncReviewModal from '../components/worklist/SyncReviewModal';
import { hasPermission } from '../utils/permissions';
import { PERMISSIONS } from '../constants/permissions';
import { formatDateTime } from '../utils/dateTime';
import { notify } from '@/components/core';
import { useBreadcrumbTitle } from '../hooks/useBreadcrumbTitle';

export default function WorklistRunnerPage({ appId, onBack, onEdit, isPreview = false }) {
    const { token, user } = useAuth();
    const containerRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    // Only admins with manage permission can pull/sync
    const isAdmin = hasPermission(user, PERMISSIONS.WORKLISTS_MANAGE);

    const [app, setApp] = useState(null);
    useBreadcrumbTitle(app?.name || null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchVal, setSearchVal] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    // Default "Assigned to Me" ON for lower-privilege roles (without manage/admin permissions)
    const defaultMyItems = !hasPermission(user, PERMISSIONS.WORKLISTS_MANAGE);
    const [myItems, setMyItems] = useState(defaultMyItems);
    const [pulling, setPulling] = useState(false);
    const [writing, setWriting] = useState(false);
    const [drawer, setDrawer] = useState(null);   // { item, audit }
    const [newItemForm, setNewItemForm] = useState(null);
    const [reviewModal, setReviewModal] = useState(false); // Pending changes review
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pendingOverall, setPendingOverall] = useState(0);
    const [highPriorityOverall, setHighPriorityOverall] = useState(0);
    const PAGE_SIZE = 50;
    const [platformUsers, setPlatformUsers] = useState([]);
    // Multi-select
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [massUpdateOpen, setMassUpdateOpen] = useState(false);
    // Dynamic column filters: [{id, col, op, value}]
    const [columnFilters, setColumnFilters] = useState([]);
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    // Column sort — init from per-user localStorage pref, app default applied after load
    const lsKey = `worklist_sort_${appId}`;
    const [sortCol, setSortCol] = useState(() => {
        try { return JSON.parse(localStorage.getItem(lsKey))?.col || null; } catch { return null; }
    });
    const [sortDir, setSortDir] = useState(() => {
        try { return JSON.parse(localStorage.getItem(lsKey))?.dir || 'asc'; } catch { return 'asc'; }
    });
    const [viewSaved, setViewSaved] = useState(false);
    // Inline revert confirm: itemId string or null
    const [revertConfirmId, setRevertConfirmId] = useState(null);
    // Comment prompt modal (replaces window.prompt)
    const [commentModal, setCommentModal] = useState(null); // { message, mandatory, resolve }
    const commentResolveRef = useRef(null);
    const requestComment = (message, mandatory = false) => new Promise(resolve => {
        commentResolveRef.current = resolve;
        setCommentModal({ message, mandatory });
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [appData, itemsData] = await Promise.all([
                interactiveFetch(`/worklists/${appId}`, token, { method: 'GET' }),
                interactiveFetch(`/worklists/${appId}/items?page=${page}&page_size=${PAGE_SIZE}${statusFilter ? `&status=${statusFilter}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}`, token, { method: 'GET' }),
            ]);
            setApp(appData);
            setItems(itemsData.items || []);
            setTotal(itemsData.total || 0);
            setPendingOverall(itemsData.pending_overall ?? 0);
            setHighPriorityOverall(itemsData.high_priority_overall ?? 0);
        } catch { notify.error('Failed to load worklist'); }
        finally { setLoading(false); }
    }, [appId, token, page, statusFilter, search]);

    useEffect(() => { load(); }, [load]);

    // Apply app-level default sort once the app config is loaded
    // Only applies when the user has no saved local preference
    useEffect(() => {
        if (!app) return;
        const saved = (() => { try { return JSON.parse(localStorage.getItem(lsKey)); } catch { return null; } })();
        const appDefault = app.default_sort_col || '';
        if (!saved?.col && appDefault) {
            setSortCol(appDefault);
            setSortDir(app.default_sort_dir || 'asc');
        }
        // Auto-enable "My Items" when record_visibility is 'assigned' (non-admins)
        if (app.record_visibility === 'assigned' && !isAdmin) {
            setMyItems(true);
        }
    }, [app?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce search typing
    useEffect(() => {
        const t = setTimeout(() => {
            if (search !== searchVal) {
                setSearch(searchVal);
                setPage(1);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [searchVal, search]);

    // Fetch platform users for assignee dropdown
    useEffect(() => {
        if (!token) return;
        apiGet('/users/', token)
            .then(users => {
                const names = (users || []).map(u => u.email || u.name).filter(Boolean);
                setPlatformUsers(names);
            })
            .catch(() => { });
    }, [token]);

    const handlePull = async (mode = 'upsert') => {
        setPulling(true);
        try {
            const stats = await interactiveFetch(`/worklists/${appId}/pull`, token, { method: 'POST', body: JSON.stringify({ upsert_mode: mode }) });
            if (stats.status === 'pulling') {
                notify.success('📡 Pull started in background — data will appear shortly. Refresh the page in a minute.', 'info');
            } else {
                notify.info(`Pull complete — ${stats.inserted ?? 0} new, ${stats.updated ?? 0} updated`);
            }
            load();
        } catch { notify.error('Pull failed'); }
        finally { setPulling(false); }
    };

    const handleWriteback = async (dryRun = false, config = {}) => {
        setWriting(true);
        try {
            const payload = {
                dry_run: dryRun,
                item_ids: config.itemIds?.length ? config.itemIds : undefined,
                writeback_mode_override: config.writeMode || undefined,
                included_fields: config.fields?.length ? config.fields : undefined,
                force_write: config.forceWrite || false,
            };
            const result = await interactiveFetch(`/worklists/${appId}/writeback`, token, { method: 'POST', body: JSON.stringify(payload) });
            if (dryRun) {
                notify.success(`Dry run: ${result.count} items would be synced`);
            } else {
                const conflictMsg = result.conflicts > 0 ? ` · ${result.conflicts} conflict${result.conflicts !== 1 ? 's' : ''} (source changed)` : '';
                notify.error(
                    `Write-back done — ${result.synced} synced, ${result.failed} failed${conflictMsg}`,
                    result.failed > 0 ? 'warning' : result.conflicts > 0 ? 'warning' : 'success'
                );
                load();
                return result;
            }
        } catch (e) { notify.error('Write-back failed: ' + (e?.message || '')); }
        finally { setWriting(false); }
        return null;
    };

    const handleEditField = async (itemId, fields) => {
        let comment = null;

        // ── Platform-managed assignee: optional comment ─────────────────
        if (fields.__platform_assignee__ !== undefined) {
            const currentItem = items.find(i => i.id === itemId);
            const currentVal = currentItem?.resolved?.__platform_assignee__ || currentItem?.field_overrides?.__platform_assignee__ || '';
            const newVal = fields.__platform_assignee__ || '';
            if (String(currentVal) !== String(newVal)) {
                const result = await requestComment('Add an optional comment for this assignment change', false);
                if (result === null) { load(); return; }
                comment = result || null;
            }
        }

        // ── Legacy source-column assignee: mandatory comment ─────────────
        const rawSch = app?.schema_map;
        const assigneeKey = Array.isArray(rawSch)
            ? rawSch.find(r => (r.core_mapping || '').toLowerCase().replace(/\s+/g, '_') === 'assignee')?.source_field
            : rawSch?.slots?.assignee;
        if (assigneeKey && fields[assigneeKey] !== undefined) {
            const currentItem = items.find(i => i.id === itemId);
            const currentVal = currentItem?.resolved?.[assigneeKey] || '';
            const newVal = fields[assigneeKey] || '';

            if (String(currentVal) !== String(newVal)) {
                const result = await requestComment('A reallocation comment is mandatory when changing the assignee. Please enter a reason:', true);
                if (result === null) {
                    load();
                    return;
                }
                if (!result.trim()) {
                    notify.error('A reallocation comment is required to change the assignee.');
                    load();
                    return;
                }
                comment = result.trim();
            }
        }

        try {
            await interactiveFetch(`/worklists/${appId}/items/${itemId}`, token, { method: 'PATCH', body: JSON.stringify({ fields, comment }) });
        } catch (err) {
            notify.error(err?.message || 'Update failed');
            return;
        }

        // PATCH succeeded — update local state (errors here are non-fatal)
        let updatedAudit = [];
        try {
            const auditResult = await interactiveFetch(`/worklists/${appId}/items/${itemId}/audit`, token, { method: 'GET' });
            if (Array.isArray(auditResult)) updatedAudit = auditResult;
        } catch (e) {
            console.warn('[Worklist] Audit refresh failed (non-fatal):', e);
        }

        setItems(prev => prev.map(i => i.id === itemId ? {
            ...i,
            resolved: { ...i.resolved, ...fields },
            field_overrides: { ...i.field_overrides, ...fields },
            writeback_status: 'pending'
        } : i));

        // If the active drawer is open, keep it in sync with new resolved values and audit history
        setDrawer(prev => {
            if (prev && prev.item.id === itemId) {
                return {
                    ...prev,
                    audit: updatedAudit.length > 0 ? updatedAudit : (prev.audit || []),
                    item: {
                        ...prev.item,
                        resolved: { ...prev.item.resolved, ...fields },
                        field_overrides: { ...prev.item.field_overrides, ...fields },
                        writeback_status: 'pending'
                    }
                };
            }
            return prev;
        });
    };

    const handleCreateItem = async () => {
        if (!newItemForm) return;
        try {
            const item = await interactiveFetch(`/worklists/${appId}/items`, token, { method: 'POST', body: JSON.stringify({ fields: newItemForm }) });
            setItems(prev => [item, ...prev]);
            setNewItemForm(null);
            notify.success('Item created — will be synced on next write-back');
        } catch { notify.error('Create failed'); }
    };

    const openDrawer = async (item) => {
        setDrawer({ item, audit: [] });
        try {
            const audit = await interactiveFetch(`/worklists/${appId}/items/${item.id}/audit`, token, { method: 'GET' });
            setDrawer({ item, audit: Array.isArray(audit) ? audit : [] });
        } catch { }
    };

    const handleRollback = async (itemId, field) => {
        if (!window.confirm(`Rollback '${field}' to its original source value?`)) return;
        try {
            const updated = await interactiveFetch(`/worklists/${appId}/items/${itemId}/overrides/${field}`, token, { method: 'DELETE' });
            // Refresh item in table and drawer
            setItems(prev => prev.map(i => i.id === itemId ? {
                ...i,
                resolved: updated,
                field_overrides: Object.fromEntries(
                    Object.entries(i.field_overrides || {}).filter(([k]) => k !== field)
                ),
                writeback_status: 'pending'
            } : i));
            setDrawer(prev => prev ? {
                ...prev,
                item: {
                    ...prev.item,
                    resolved: updated,
                    field_overrides: Object.fromEntries(
                        Object.entries(prev.item.field_overrides || {}).filter(([k]) => k !== field)
                    ),
                    writeback_status: 'pending'
                }
            } : prev);
            notify.error(`'${field}' rolled back to source value`);
        } catch { notify.error('Rollback failed'); }
    };

    // ── Revert ALL overrides for a single row ──────────────────────────────
    const handleRevertAll = async (itemId) => {
        try {
            const restored = await interactiveFetch(`/worklists/${appId}/items/${itemId}/overrides`, token, { method: 'DELETE' });
            setItems(prev => prev.map(i => i.id === itemId ? {
                ...i,
                resolved: restored,
                field_overrides: {},
                writeback_status: 'synced',
            } : i));
            setDrawer(prev => prev?.item?.id === itemId ? {
                ...prev,
                item: { ...prev.item, resolved: restored, field_overrides: {}, writeback_status: 'synced' }
            } : prev);
            setRevertConfirmId(null);
            notify.success('Row reverted to source values');
        } catch { notify.error('Revert failed'); }
    };

    // ── Mass Update: batch PATCH in groups of 10, skip+report failures ─────
    const handleMassUpdate = async (fields) => {
        if (!selectedIds.size || !Object.keys(fields).length) return;
        const ids = [...selectedIds];
        let ok = 0, failed = 0;
        const BATCH = 10;
        for (let i = 0; i < ids.length; i += BATCH) {
            const chunk = ids.slice(i, i + BATCH);
            await Promise.all(chunk.map(async (itemId) => {
                try {
                    const updated = await interactiveFetch(`/worklists/${appId}/items/${itemId}`, token, { method: 'PATCH', body: JSON.stringify({ fields }) });
                    setItems(prev => prev.map(it => it.id === itemId ? {
                        ...it,
                        resolved: { ...it.resolved, ...fields },
                        writeback_status: 'pending',
                    } : it));
                    ok++;
                } catch { failed++; }
            }));
        }
        const msg = failed
            ? `${ok} updated, ${failed} failed`
            : `${ok} item${ok !== 1 ? 's' : ''} updated`;
        notify.error(msg, failed ? 'warning' : 'success');
        setMassUpdateOpen(false);
        setSelectionMode(false);
        setSelectedIds(new Set());
    };

    if (loading && !app) return (
        <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-slate-950">
            <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-9 h-9 animate-spin text-indigo-650 dark:text-indigo-400" />
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest animate-pulse">Loading Workspace...</span>
            </div>
        </div>
    );

    // ── Schema parsing: support new flat array format AND old slot-based format ──
    const rawSchema = app?.schema_map;
    const isArraySchema = Array.isArray(rawSchema);

    // ── NEW format: flat array from Step 2 ──────────────────────────────────
    // Each row: { source_field, is_selected, is_read_only, field_type, core_mapping, options }
    let slots = {};
    let statuses = app?.workflow_config?.statuses || ['Open', 'In Progress', 'Closed'];
    let allCols = [];
    let isColEditable = () => false;

    if (isArraySchema) {
        const selectedRows = rawSchema.filter(r => r.is_selected !== false);

        // Derive slots from core_mapping for backward compat features (assignee filter, etc.)
        // Note: Status is now platform-managed (__platform_status__) — not derived from core_mapping
        selectedRows.forEach(r => {
            const cm = (r.core_mapping || '').toLowerCase().replace(/\s+/g, '_');
            if (cm === 'primary_key') slots.pk = r.source_field;
            if (cm === 'title') slots.title = r.source_field;
            if (cm === 'assignee') slots.assignee = r.source_field;
            if (cm === 'due_date') slots.due_date = r.source_field;
        });

        // Build cols directly from selected rows — order preserved from Step 2
        allCols = selectedRows.map(r => {
            const ft = (r.field_type || 'As-Is').toLowerCase();
            let type = 'text';
            if (ft.includes('drop') || ft.includes('radio') || ft.includes('single')) {
                type = 'dropdown';
            } else if (ft.includes('multi')) {
                type = 'multi';
            } else if (ft.includes('date time') || ft.includes('datetime')) {
                type = 'datetime-local';
            } else if (ft.includes('date')) {
                type = 'date';
            } else if (ft.includes('time')) {
                type = 'time';
            } else if (ft.includes('duration')) {
                type = 'duration';
            } else if (ft.includes('currency')) {
                type = 'currency';
            } else if (ft.includes('percentage') || ft.includes('%')) {
                type = 'percentage';
            } else if (ft.includes('whole') || ft.includes('decimal') || ft.includes('number') || ft.includes('numeric')) {
                type = 'number';
            } else if (ft.includes('check') || ft.includes('boolean') || ft.includes('toggle')) {
                type = 'checkbox';
            } else if (ft.includes('area') || ft.includes('rich') || ft.includes('textarea')) {
                type = 'textarea';
            } else if (ft.includes('url')) {
                type = 'url';
            } else if (ft.includes('email')) {
                type = 'email';
            } else if (ft.includes('phone')) {
                type = 'phone';
            }

            let opts = r.options || '';

            return {
                key: r.source_field,
                label: r.source_field,
                type,
                options: opts,
                readOnly: !!r.is_read_only,
                slotName: Object.entries(slots).find(([, v]) => v === r.source_field)?.[0] || null,
            };
        });

        const isAdminRole = hasPermission(user, PERMISSIONS.WORKLISTS_MANAGE);
        isColEditable = (col) => {
            // is_read_only from Step 2 is ABSOLUTE — not even admins can bypass it.
            // It means the field is display-only (e.g. ID, primary key, source reference).
            // Admin privilege only bypasses record-level terminal-status locks, not field config.
            if (col.readOnly) return false;
            return true;  // Editable for everyone (record-level lock handled separately in WorklistRow)
        };
        // Expose isAdminRole so WorklistRow can bypass record-level lock for admins
        allCols = allCols.map(c => ({ ...c, _isAdminRole: isAdminRole }));

    } else {
        // ── OLD format: slot-based object (backward compat) ─────────────────
        const schema = rawSchema || {};
        slots = schema.slots || {};
        statuses = app?.workflow_config?.statuses || ['Open', 'In Progress', 'Closed'];

        const configuredPool = app?.workflow_config?.assignee_pool || [];
        const assigneePool = configuredPool.length > 0
            ? configuredPool
            : platformUsers.length > 0
                ? platformUsers
                : (() => {
                    const assigneeKey = schema?.slots?.assignee;
                    if (!assigneeKey || !items?.length) return [];
                    return [...new Set(items.map(i => i.resolved?.[assigneeKey]).filter(Boolean))].sort();
                })();

        const slotCols = [
            slots.title && { key: slots.title, slotName: 'title', label: 'Title', type: 'text' },
            slots.status && { key: slots.status, slotName: 'status', label: 'Status', type: 'dropdown', options: statuses.join(',') },
            slots.assignee && { key: slots.assignee, slotName: 'assignee', label: 'Assignee', type: 'dropdown', options: assigneePool.join(',') },
            slots.due_date && { key: slots.due_date, slotName: 'due_date', label: 'Due Date', type: 'date' },
            slots.amount && { key: slots.amount, slotName: 'amount', label: 'Amount', type: 'number' },
            ...(schema.custom_fields || []).map(cf => typeof cf === 'string'
                ? { key: cf, label: cf, type: 'text', isCustom: true }
                : { key: cf.source_col, label: cf.label || cf.source_col, type: cf.type || 'text', options: cf.options || '', isCustom: true }
            )
        ].filter(Boolean);

        // Extra raw source columns not mapped — show read-only
        const mappedKeys = new Set(slotCols.map(c => c.key));
        const firstResolved = items[0]?.resolved || {};
        const extraCols = Object.keys(firstResolved)
            .filter(k => !k.startsWith('__') && !mappedKeys.has(k))
            .map(k => ({ key: k, label: k, type: 'text', isExtra: true, slotName: null }));
        allCols = [...slotCols, ...extraCols];

        const editableSlots = schema.editable_slots || ['title', 'status', 'assignee', 'due_date', 'amount', 'notes'];
        const editableCustom = schema.editable_custom || {};
        const roleId = user?.roleId || '';
        const isPrivilegedRole = hasPermission(user, PERMISSIONS.WORKLISTS_MANAGE);

        isColEditable = (col) => {
            if (col.isExtra) return false;
            if (isPrivilegedRole) return true;
            if (col.isCustom) {
                if (Array.isArray(editableCustom)) return editableCustom.includes(col.key);
                return (editableCustom[col.key] || []).includes(roleId);
            }
            if (Array.isArray(editableSlots)) return editableSlots.includes(col.slotName);
            return (editableSlots[col.slotName] || []).includes(roleId);
        };
    }

    // ── Resolve assignee pool (used by both schema formats) ────────────
    const configuredPool = app?.workflow_config?.assignee_pool || [];
    const assigneePool = configuredPool.length > 0
        ? configuredPool
        : platformUsers.length > 0
            ? platformUsers
            : (() => {
                const ak = slots.assignee;
                if (!ak || !items?.length) return [];
                return [...new Set(items.map(i => i.resolved?.[ak]).filter(Boolean))].sort();
            })();

    // Platform-managed assignee: derive pool from existing assignments too
    const platformAssigneePool = (() => {
        if (assigneePool.length > 0) return assigneePool;
        // Fallback: collect unique values already assigned in __platform_assignee__
        if (!items?.length) return [];
        return [...new Set(items.map(i => i.resolved?.__platform_assignee__).filter(Boolean))].sort();
    })();

    // ── System columns visibility (configured in Step 2) ──────────────
    const sysCols = new Set(app?.workflow_config?.system_columns || []);
    const customCols = app?.workflow_config?.custom_columns || [];
    const sysColCount = sysCols.size + 1 + customCols.length; // +1 for always-visible Sync State

    // Apply filters: Assigned to Me + dynamic column filters
    const userEmail = token ? (() => { try { return JSON.parse(atob(token.split('.')[1]))?.email; } catch { return ''; } })() : '';

    // Column filter operator logic
    const applyOp = (cellVal, op, filterVal) => {
        const s = String(cellVal ?? '').toLowerCase();
        const f = String(filterVal ?? '').toLowerCase();
        switch (op) {
            case 'eq': return s === f;
            case 'neq': return s !== f;
            case 'contains': return s.includes(f);
            case 'starts': return s.startsWith(f);
            case 'gt': return parseFloat(cellVal) > parseFloat(filterVal);
            case 'lt': return parseFloat(cellVal) < parseFloat(filterVal);
            case 'empty': return !cellVal || cellVal === '';
            case 'not_empty': return !!cellVal && cellVal !== '';
            default: return true;
        }
    };

    const filteredItems = items.filter(item => {
        // Assigned to Me filter — checks platform-managed assignee field
        if (myItems) {
            const assigneeVal = item.resolved?.__platform_assignee__ || item.field_overrides?.__platform_assignee__;
            if (String(assigneeVal || '').toLowerCase() !== String(userEmail || '').toLowerCase()) return false;
        }
        // Dynamic column filters (AND)
        for (const f of columnFilters) {
            if (!f.col) continue;
            // Filter condition based on original source columns
            const cellVal = item.data?.[f.col] ?? item.resolved?.[f.col];
            if (!applyOp(cellVal, f.op, f.value)) return false;
        }
        return true;
    });

    // ── Client-side sort applied on top of filtered results ──────────────────
    const sortedItems = (() => {
        if (!sortCol) return filteredItems;
        return [...filteredItems].sort((a, b) => {
            // Coerce to primitive — Excel numbers come back as JS numbers (e.g. 1001)
            const rawA = a.resolved?.[sortCol] ?? a.data?.[sortCol] ?? '';
            const rawB = b.resolved?.[sortCol] ?? b.data?.[sortCol] ?? '';
            const va = rawA === null || rawA === undefined ? '' : rawA;
            const vb = rawB === null || rawB === undefined ? '' : rawB;
            // Numeric comparison when both values are numbers or numeric strings
            const na = Number(va), nb = Number(vb);
            let cmp;
            if (!isNaN(na) && !isNaN(nb) && va !== '' && vb !== '') {
                cmp = na - nb;
            } else {
                cmp = String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
    })();

    const handleSort = (key) => {
        if (sortCol === key) {
            if (sortDir === 'asc') setSortDir('desc');
            else { setSortCol(null); setSortDir('asc'); }   // third click clears sort
        } else {
            setSortCol(key);
            setSortDir('asc');
        }
    };

    const pendingCount = items.filter(i => i.writeback_status === 'pending').length;

    return (
        <div ref={containerRef} className="flex flex-col h-full bg-gray-50 dark:bg-slate-950 overflow-hidden font-sans">
            {/* Header */}
            <div className="flex-none px-6 py-4 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800">
                {/* Top row: back + title + actions */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <button onClick={onBack} className="p-2 rounded-xl text-gray-500 hover:text-gray-800 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                        <div>
                            <h1 className="text-lg font-black text-gray-900 dark:text-slate-100 leading-tight">Insights-Driven Worklist Dashboard</h1>
                            <p className="text-[11px] font-medium text-gray-400 dark:text-slate-500 mt-0.5">{app?.name}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Add Entry */}
                        {!isPreview && (form => {
                            const policy = app?.allow_add_entry || 'all';
                            if (policy === 'none') return null;
                            return (
                                <button onClick={() => setNewItemForm({})}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white
                                        bg-gradient-to-br from-emerald-500 to-teal-600 shadow-sm
                                        hover:shadow-md hover:-translate-y-px active:translate-y-0 transition-all">
                                    <Plus className="w-3.5 h-3.5" /> Add Entry
                                </button>
                            );
                        })()}

                        {/* Fullscreen toggle */}
                        <button
                            onClick={toggleFullscreen}
                            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                            className="p-2 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400
                                hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-300 dark:hover:border-indigo-700
                                hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
                        >
                            {isFullscreen
                                ? <Minimize2 className="w-4 h-4" />
                                : <Maximize2 className="w-4 h-4" />}
                        </button>

                        {!isPreview && <div className="h-5 w-px bg-gray-200 dark:bg-slate-700" />}

                        {/* Pull + Sync — controlled by Editor Step 3/4 config */}
                        {!isPreview && (() => {
                            const pullPolicy = app?.allow_pull || 'all';
                            const canPull = pullPolicy !== 'none';
                            const isPlatform = app?.writeback_sink?.type === 'platform';

                            return (<>
                                {canPull && <PullMenu onPull={handlePull} loading={pulling} />}
                                {!isPlatform ? (
                                    <button onClick={() => setReviewModal(true)} disabled={writing || pendingCount === 0}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white
                                                bg-gradient-to-br from-violet-600 to-indigo-700 shadow-sm
                                                hover:shadow-md hover:-translate-y-px active:translate-y-0 transition-all
                                                disabled:opacity-50 disabled:pointer-events-none">
                                        {writing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                        Sync Changes
                                        {pendingCount > 0 && (
                                            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/30 text-[9px] font-black">{pendingCount > 99 ? '99+' : pendingCount}</span>
                                        )}
                                    </button>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40">
                                        🔒 Platform-managed
                                    </span>
                                )}
                            </>);
                        })()}

                        {isPreview && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40">
                                👁 Preview — actions disabled
                            </span>
                        )}
                    </div>
                </div>

                {/* KPI Bar */}
                <WorklistKPIBar items={items} statuses={statuses} pendingCount={pendingCount} totalOverall={total} pendingOverall={pendingOverall} highPriorityOverall={highPriorityOverall} pageTotal={items.length} />
            </div>


            {/* Sidebar + Content Layout */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Filter Sidebar */}
                <WorklistFilterPanel
                    isOpen={filterPanelOpen}
                    onClose={() => setFilterPanelOpen(false)}
                    cols={allCols}
                    filters={columnFilters}
                    setFilters={setColumnFilters}
                />

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Search bar + quick actions */}
                    <div className="flex-none px-5 py-2.5 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-1 flex-wrap">
                            <button onClick={() => setFilterPanelOpen(v => !v)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${filterPanelOpen || columnFilters.length > 0
                                ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border-indigo-300 dark:border-indigo-700'
                                : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-700 hover:border-indigo-400'
                                }`}>
                                <Filter className="w-3.5 h-3.5" /> Filters {columnFilters.length > 0 && `(${columnFilters.length})`}
                            </button>
                            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                                className="text-xs px-3 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg outline-none dark:text-slate-100 transition-all">
                                <option value="">All Statuses</option>
                                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <button onClick={() => { setMyItems(v => !v); setPage(1); }}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${myItems ? 'bg-indigo-600 border-indigo-700 text-white' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:border-indigo-400'
                                    }`}>
                                👤 My Items
                            </button>
                            {!isPreview && (
                                <button onClick={() => { setSelectionMode(v => !v); setSelectedIds(new Set()); }}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${selectionMode ? 'bg-violet-600 border-violet-700 text-white' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:border-violet-400'
                                        }`}>
                                    <CheckSquare className="w-3.5 h-3.5" />
                                    {selectionMode ? `${selectedIds.size} selected` : 'Select'}
                                </button>
                            )}
                            {sortCol && (
                                <button onClick={() => {
                                    try { localStorage.setItem(lsKey, JSON.stringify({ col: sortCol, dir: sortDir })); } catch { }
                                    setViewSaved(true); setTimeout(() => setViewSaved(false), 1800);
                                }}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold transition-all ${viewSaved ? 'bg-emerald-50 border-emerald-400 text-emerald-600' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-500 hover:border-indigo-400'
                                        }`}>
                                    {viewSaved ? '✓ Saved' : '💾 Save View'}
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <input type="text" placeholder="Search…" value={searchVal}
                                    onChange={e => setSearchVal(e.target.value)}
                                    className="pl-9 pr-3 py-1.5 text-xs bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg outline-none focus:border-indigo-400 transition-all w-44 dark:text-slate-100" />
                            </div>
                            <button onClick={load} className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border border-gray-200 dark:border-slate-700 transition-all" title="Refresh">
                                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                    </div>

                    {/* Table area */}
                    <div className="flex-1 overflow-hidden p-4 flex flex-col gap-3">
                        <div className="flex-1 bg-white dark:bg-slate-900 rounded-2xl border border-gray-150 dark:border-slate-800/80 overflow-auto shadow-sm custom-scrollbar isolate">
                            <table className="min-w-max w-full text-sm">
                                <thead className="sticky top-0 z-10">
                                    <tr className="border-b border-gray-150 dark:border-slate-850 bg-gray-50 dark:bg-slate-900 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                                        {/* Checkbox select-all column */}
                                        {selectionMode && (
                                            <th className="pl-5 pr-2 py-3.5 w-9">
                                                <input type="checkbox"
                                                    className="w-4 h-4 rounded accent-violet-600 cursor-pointer"
                                                    checked={filteredItems.length > 0 && filteredItems.every(i => selectedIds.has(i.id))}
                                                    onChange={e => {
                                                        if (e.target.checked) setSelectedIds(new Set(filteredItems.map(i => i.id)));
                                                        else setSelectedIds(new Set());
                                                    }} />
                                            </th>
                                        )}
                                        {allCols.map(col => (
                                            <th key={col.key}
                                                onClick={() => handleSort(col.key)}
                                                className={`min-w-[150px] px-5 py-3.5 text-left text-[10px] font-extrabold uppercase tracking-widest cursor-pointer select-none transition-colors ${sortCol === col.key
                                                    ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/40 dark:bg-indigo-950/20'
                                                    : col.isExtra ? 'text-gray-300 dark:text-slate-700 hover:text-gray-400' : 'text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300'
                                                    }`}>
                                                <span className="flex items-center gap-1 group">
                                                    {col.label}
                                                    {!isColEditable(col) && !col.isExtra && <span title="Read-only" className="text-gray-300 dark:text-slate-700">🔒</span>}
                                                    {sortCol === col.key ? (
                                                        sortDir === 'asc'
                                                            ? <span className="text-indigo-500 text-[10px] font-black">▲</span>
                                                            : <span className="text-indigo-500 text-[10px] font-black">▼</span>
                                                    ) : (
                                                        <span className="text-gray-300 dark:text-slate-700 text-[9px] opacity-0 group-hover:opacity-100 transition-opacity">⇅</span>
                                                    )}
                                                </span>
                                            </th>
                                        ))}
                                        {/* ── System Audit Columns ── */}
                                        {sysCols.has('modified_by') && (
                                            <th className="px-4 py-3.5 text-[10px] font-extrabold text-violet-400 dark:text-violet-500 uppercase tracking-widest whitespace-nowrap">
                                                <span className="flex items-center gap-1">👤 Modified By</span>
                                            </th>
                                        )}
                                        {sysCols.has('remarks') && (
                                            <th className="px-4 py-3.5 text-[10px] font-extrabold text-violet-400 dark:text-violet-500 uppercase tracking-widest min-w-[180px]">
                                                <span className="flex items-center gap-1">💬 Remarks</span>
                                            </th>
                                        )}
                                        {sysCols.has('modified_at') && (
                                            <th className="px-4 py-3.5 text-[10px] font-extrabold text-violet-400 dark:text-violet-500 uppercase tracking-widest whitespace-nowrap">
                                                <span className="flex items-center gap-1">🕐 Modified At</span>
                                            </th>
                                        )}
                                        {/* ── Custom Platform Columns ── */}
                                        {customCols.map(cc => (
                                            <th key={cc.key} className="px-4 py-3.5 text-[10px] font-extrabold text-amber-500 dark:text-amber-400 uppercase tracking-widest whitespace-nowrap">
                                                <span className="flex items-center gap-1">✨ {cc.label}</span>
                                            </th>
                                        ))}

                                        {/* ── Sticky Action Columns (far right) ── */}
                                        {sysCols.has('status') && (
                                            <th style={{ right: `${sysCols.has('assignee') ? 160 + 180 : 160}px` }}
                                                className="sticky z-20 bg-gray-50 dark:bg-slate-900 border-l border-gray-150 dark:border-slate-800 shadow-[-2px_0_5px_rgba(0,0,0,0.03)] px-4 py-3.5 text-[10px] font-extrabold text-indigo-500 dark:text-indigo-400 uppercase tracking-widest whitespace-nowrap w-[150px] min-w-[150px] max-w-[150px] text-center">
                                                <span className="flex items-center justify-center gap-1">🔄 Status</span>
                                            </th>
                                        )}
                                        {sysCols.has('assignee') && (
                                            <th style={{ right: '160px' }}
                                                className="sticky z-20 bg-gray-50 dark:bg-slate-900 border-l border-gray-150 dark:border-slate-800 shadow-[-2px_0_5px_rgba(0,0,0,0.03)] px-4 py-3.5 text-[10px] font-extrabold text-emerald-500 dark:text-emerald-400 uppercase tracking-widest whitespace-nowrap w-[180px] min-w-[180px] max-w-[180px] text-center">
                                                <span className="flex items-center justify-center gap-1">📋 Assignee</span>
                                            </th>
                                        )}
                                        <th style={{ right: '0px' }}
                                            className="sticky right-0 z-20 bg-gray-50 dark:bg-slate-900 border-l border-gray-150 dark:border-slate-800 shadow-[-2px_0_5px_rgba(0,0,0,0.03)] px-5 py-3.5 text-[10px] font-extrabold text-gray-400 dark:text-slate-500 uppercase tracking-widest text-right w-[160px] min-w-[160px] max-w-[160px]">
                                            Sync State
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-slate-850">
                                    {filteredItems.length === 0 ? (
                                        <tr>
                                            <td colSpan={allCols.length + sysColCount + (selectionMode ? 1 : 0)} className="px-6 py-20 text-center">
                                                <div className="flex flex-col items-center gap-2 max-w-sm mx-auto">
                                                    <ClipboardList className="w-10 h-10 text-gray-300 dark:text-slate-700" />
                                                    <h3 className="text-sm font-bold text-gray-800 dark:text-slate-200">No matching entries</h3>
                                                    <p className="text-xs text-gray-400">Pull data from the source, create a new entry manually, or adjust your search filters.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : sortedItems.map(item => (
                                        <WorklistRow
                                            key={item.id}
                                            item={item}
                                            cols={allCols}
                                            slots={slots}
                                            statuses={statuses}
                                            terminalStatuses={app?.workflow_config?.terminal_statuses || ['Closed']}
                                            onEdit={handleEditField}
                                            onOpen={() => { if (!selectionMode) openDrawer(item); }}
                                            isColEditable={isColEditable}
                                            assigneePool={platformAssigneePool}
                                            sysCols={sysCols}
                                            customCols={customCols}
                                            selectionMode={selectionMode}
                                            selected={selectedIds.has(item.id)}
                                            isActive={drawer?.item?.id === item.id}
                                            onToggleSelect={() => setSelectedIds(prev => {
                                                const next = new Set(prev);
                                                next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                                                return next;
                                            })}
                                            onRevert={handleRevertAll}
                                            revertConfirmId={revertConfirmId}
                                            setRevertConfirmId={setRevertConfirmId}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <WorklistPagination page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
                    </div>{/* end table area */}
                </div>{/* end main content */}
            </div>{/* end sidebar+content */}

            {/* ── Floating Selection Action Bar ──────────────────────────────────── */}
            {selectionMode && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3
                    bg-slate-900/95 dark:bg-slate-800/95 backdrop-blur-md text-white
                    px-5 py-3 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.35)]
                    border border-white/10 animate-in slide-in-from-bottom-4 duration-200">
                    {/* Select All filtered */}
                    <button onClick={() => setSelectedIds(new Set(filteredItems.map(i => i.id)))}
                        className="text-[11px] font-bold text-slate-400 hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-white/10">
                        Select All ({filteredItems.length})
                    </button>
                    <div className="h-4 w-px bg-white/20" />
                    <span className="text-[11px] font-bold text-white">
                        {selectedIds.size} record{selectedIds.size !== 1 ? 's' : ''} selected
                    </span>
                    <div className="h-4 w-px bg-white/20" />
                    <button onClick={() => setMassUpdateOpen(true)} disabled={selectedIds.size === 0}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-xl text-xs font-bold
                            bg-gradient-to-br from-violet-500 to-indigo-600 text-white
                            shadow-[0_2px_8px_rgba(99,102,241,0.4)]
                            hover:shadow-[0_4px_16px_rgba(99,102,241,0.5)]
                            hover:-translate-y-px active:translate-y-0 transition-all
                            disabled:opacity-50 disabled:pointer-events-none">
                        <Layers className="w-3.5 h-3.5" /> Mass Update
                    </button>
                    <button onClick={() => { setSelectionMode(false); setSelectedIds(new Set()); }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* New Item Modal */}
            {newItemForm !== null && (
                <NewItemModal
                    cols={allCols.filter(c =>
                        !c.readOnly &&            // Skip read-only fields (FLOW_ID, TENANT_ID, STATUS, etc.)
                        c.slotName !== 'pk' &&    // Skip primary key (auto-generated by DB)
                        !c.isExtra &&             // Skip extra unmapped source columns
                        !c.key.startsWith('__')   // Skip internal/system fields
                    )}
                    statuses={statuses}
                    slots={slots}
                    assigneePool={assigneePool}
                    onSave={handleCreateItem}
                    onClose={() => setNewItemForm(null)}
                    form={newItemForm}
                    setForm={setNewItemForm}
                    sysCols={sysCols}
                    platformAssigneePool={platformAssigneePool}
                />
            )}

            {/* Audit & Edit Side Drawer */}
            {drawer && (
                <WorklistDrawer
                    item={drawer.item}
                    audit={drawer.audit}
                    slots={slots}
                    cols={allCols}
                    statuses={statuses}
                    assigneePool={assigneePool}
                    onEdit={handleEditField}
                    onRollback={handleRollback}
                    onClose={() => setDrawer(null)}
                    isColEditable={isColEditable}
                />
            )}



            {/* Sync Review Modal */}
            {reviewModal && (
                <SyncReviewModal
                    items={items.filter(i => i.writeback_status === 'pending')}
                    slots={slots}
                    app={app}
                    writing={writing}
                    onConfirm={async (cfg) => {
                        setReviewModal(false);
                        const result = await handleWriteback(false, cfg);
                        // If conflicts, re-open modal with conflict info so user can force-write
                        if (result?.conflicts > 0) {
                            setReviewModal(true);
                        }
                    }}
                    onDryRun={async (cfg) => {
                        await handleWriteback(true, cfg);
                    }}
                    onClose={() => setReviewModal(false)}
                />
            )}

            {/* Mass Update Modal */}
            {massUpdateOpen && (
                <MassUpdateModal
                    cols={allCols.filter(c => isColEditable(c))}
                    statuses={statuses}
                    selectedCount={selectedIds.size}
                    onApply={handleMassUpdate}
                    onClose={() => setMassUpdateOpen(false)}
                    sysCols={sysCols}
                    assigneePool={platformAssigneePool}
                    customCols={customCols}
                />
            )}

            {/* Comment Prompt Modal (replaces window.prompt) */}
            {commentModal && (() => {
                const { message, mandatory } = commentModal;
                const CommentModalInner = () => {
                    const [val, setVal] = useState('');
                    return (
                        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-gray-150 dark:border-slate-800 shadow-[0_25px_80px_rgba(0,0,0,0.2)] w-full max-w-md overflow-hidden">
                                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-slate-800">
                                    <h2 className="text-sm font-extrabold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                                        💬 {mandatory ? 'Comment Required' : 'Add Comment'}
                                    </h2>
                                    <button onClick={() => { commentResolveRef.current?.(null); setCommentModal(null); }}
                                        className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="px-6 py-5 space-y-4">
                                    <p className="text-xs text-gray-500 dark:text-slate-400">{message}</p>
                                    <textarea
                                        autoFocus rows={3} value={val}
                                        onChange={e => setVal(e.target.value)}
                                        placeholder={mandatory ? 'Enter a reason…' : 'Optional comment…'}
                                        className="w-full text-xs px-3 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700
                                            bg-gray-50/50 dark:bg-slate-800/50 dark:text-slate-100
                                            outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10
                                            transition-all resize-none placeholder:text-gray-300 dark:placeholder:text-slate-600"
                                    />
                                </div>
                                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-800/20">
                                    <button onClick={() => { commentResolveRef.current?.(null); setCommentModal(null); }}
                                        className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 dark:text-slate-400
                                            hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800
                                            border border-transparent hover:border-gray-200 dark:hover:border-slate-700 transition-all">
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => { if (mandatory && !val.trim()) return; commentResolveRef.current?.(val); setCommentModal(null); }}
                                        disabled={mandatory && !val.trim()}
                                        className="px-5 py-2 rounded-xl text-xs font-bold text-white
                                            bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed
                                            border border-indigo-700 transition-all shadow-sm">
                                        {mandatory ? 'Submit' : 'Continue'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                };
                return <CommentModalInner />;
            })()}
        </div>
    );
}

function NewItemModal({ cols, statuses, slots, assigneePool, form, setForm, onSave, onClose, sysCols = new Set(), platformAssigneePool = [] }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-gray-150 dark:border-slate-800 w-full max-w-md p-6 space-y-5 animate-slide-up">
                <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-800/80 pb-3">
                    <h3 className="font-extrabold text-gray-900 dark:text-slate-100 text-base leading-tight tracking-tight">Add New Entry</h3>
                    <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all"><X className="w-4 h-4" /></button>
                </div>
                <div className="space-y-4 max-h-80 overflow-y-auto pr-1.5 custom-scrollbar">
                    {cols.length === 0 && (
                        <p className="text-xs text-gray-400 dark:text-slate-500 text-center py-6">
                            No editable columns configured. Check the Worklist Editor (Step 2) to ensure columns are selected and not all marked as read-only.
                        </p>
                    )}
                    {cols.map(col => (
                        <div key={col.key} className="space-y-1.5">
                            <label className="block text-[10px] font-extrabold text-gray-400 dark:text-slate-500 uppercase tracking-wide">{col.label}</label>
                            {col.type === 'dropdown' ? (
                                <select value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))}
                                    className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                    <option value="">— Select Option —</option>
                                    {(col.options || '').split(',').map(o => o.trim()).filter(Boolean).map(o => (
                                        <option key={o} value={o}>{o}</option>
                                    ))}
                                </select>
                            ) : col.type === 'textarea' ? (
                                <textarea rows={3} className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all resize-none"
                                    value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))} />
                            ) : col.type === 'checkbox' ? (
                                <div className="flex items-center gap-2 pt-1">
                                    <input type="checkbox" checked={!!form[col.key]} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.checked }))}
                                        className="rounded border-gray-300 text-indigo-650 focus:ring-indigo-500 h-4.5 w-4.5 bg-white dark:bg-slate-950" />
                                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-300">{form[col.key] ? 'Checked' : 'Unchecked'}</span>
                                </div>
                            ) : col.type === 'date' || col.type === 'datetime-local' || col.type === 'time' ? (
                                <input type={col.type} className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                    value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))} />
                            ) : col.type === 'number' || col.type === 'currency' || col.type === 'percentage' ? (
                                <input type="number" step="any" className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                    placeholder={col.type === 'currency' ? '0.00' : col.type === 'percentage' ? '0' : ''}
                                    value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))} />
                            ) : (
                                <input type={col.type === 'url' ? 'url' : col.type === 'email' ? 'email' : col.type === 'phone' ? 'tel' : 'text'}
                                    className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                    placeholder={col.label}
                                    value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))} />
                            )}
                        </div>
                    ))}

                    {/* Platform-managed Status */}
                    {sysCols.has('status') && (
                        <div className="space-y-1.5 pt-2 border-t border-gray-100 dark:border-slate-800">
                            <label className="block text-[10px] font-extrabold text-indigo-500 dark:text-indigo-400 uppercase tracking-wide">🔄 Status</label>
                            <select value={form.__platform_status__ || ''} onChange={e => setForm(f => ({ ...f, __platform_status__: e.target.value }))}
                                className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-950 border border-indigo-200 dark:border-indigo-800/40 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                <option value="">— Select Status —</option>
                                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Platform-managed Assignee */}
                    {sysCols.has('assignee') && (
                        <div className="space-y-1.5">
                            <label className="block text-[10px] font-extrabold text-emerald-500 dark:text-emerald-400 uppercase tracking-wide">📋 Assignee</label>
                            {platformAssigneePool.length > 0 ? (
                                <select value={form.__platform_assignee__ || ''} onChange={e => setForm(f => ({ ...f, __platform_assignee__: e.target.value }))}
                                    className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-950 border border-emerald-200 dark:border-emerald-800/40 rounded-xl outline-none dark:text-slate-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all">
                                    <option value="">— Select Assignee —</option>
                                    {platformAssigneePool.map(a => <option key={a} value={a}>{a}</option>)}
                                </select>
                            ) : (
                                <input className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-950 border border-emerald-200 dark:border-emerald-800/40 rounded-xl outline-none dark:text-slate-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all"
                                    placeholder="Enter assignee email"
                                    value={form.__platform_assignee__ || ''} onChange={e => setForm(f => ({ ...f, __platform_assignee__: e.target.value }))} />
                            )}
                        </div>
                    )}
                </div>
                <div className="flex gap-3 pt-3">
                    <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs font-bold text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">Cancel</button>
                    <button onClick={onSave} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-br from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-xl text-xs font-extrabold transition-all shadow-md shadow-indigo-500/20">
                        <Save className="w-4 h-4" /> Save Entry
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Mass Update Modal ──────────────────────────────────────────────────────────
function MassUpdateModal({ cols, statuses, selectedCount, onApply, onClose, sysCols = new Set(), assigneePool = [], customCols = [] }) {
    const [applying, setApplying] = useState(false);
    const [fieldUpdates, setFieldUpdates] = useState([]);
    const [addKey, setAddKey] = useState('');

    // Build combined field list: source columns + enabled system columns + custom columns
    const allFields = [
        ...cols,
        ...(sysCols.has('status') ? [{ key: '__platform_status__', label: '🔄 Status', type: 'dropdown', options: (statuses || []).join(','), _isSystem: true }] : []),
        ...(sysCols.has('assignee') ? [{ key: '__platform_assignee__', label: '📋 Assignee', type: 'dropdown', options: (assigneePool || []).join(','), _isSystem: true }] : []),
        ...(customCols || []).map(cc => ({ key: cc.key, label: `✨ ${cc.label}`, type: cc.type || 'text', options: cc.options || '', _isSystem: true })),
    ];

    const addField = () => {
        if (!addKey || fieldUpdates.find(f => f.colKey === addKey)) return;
        setFieldUpdates(prev => [...prev, { colKey: addKey, value: '' }]);
        setAddKey('');
    };

    const handleApply = async () => {
        if (!fieldUpdates.length) return;
        const fields = Object.fromEntries(fieldUpdates.map(f => [f.colKey, f.value]));
        setApplying(true);
        await onApply(fields);
        setApplying(false);
    };

    const colByKey = (key) => allFields.find(c => c.key === key);

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
            <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-gray-150 dark:border-slate-800 overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-slate-800">
                    <div className="w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                        <Layers className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-sm font-extrabold text-gray-900 dark:text-slate-100">Mass Update</h2>
                        <p className="text-[11px] text-gray-400">{selectedCount} record{selectedCount !== 1 ? 's' : ''} will be updated</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Field list */}
                <div className="px-6 py-4 space-y-3 max-h-[320px] overflow-y-auto">
                    {fieldUpdates.length === 0 && (
                        <p className="text-[11px] text-gray-400 text-center py-4">No fields added. Use the picker below to add fields to update.</p>
                    )}
                    {fieldUpdates.map((fu, idx) => {
                        const col = colByKey(fu.colKey);
                        const opts = col?.type === 'dropdown' ? (col.options || '').split(',').map(o => o.trim()).filter(Boolean) : [];
                        return (
                            <div key={fu.colKey} className="flex items-center gap-2">
                                <span className="text-xs font-bold text-gray-600 dark:text-slate-300 w-28 shrink-0 truncate">{col?.label || fu.colKey}</span>
                                {col?.type === 'dropdown' ? (
                                    <select value={fu.value}
                                        onChange={e => setFieldUpdates(prev => prev.map((f, i) => i === idx ? { ...f, value: e.target.value } : f))}
                                        className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-violet-400">
                                        <option value="">— select —</option>
                                        {opts.map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                ) : col?.type === 'date' ? (
                                    <input type="date" value={fu.value}
                                        onChange={e => setFieldUpdates(prev => prev.map((f, i) => i === idx ? { ...f, value: e.target.value } : f))}
                                        className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-violet-400" />
                                ) : col?.type === 'number' ? (
                                    <input type="number" value={fu.value}
                                        onChange={e => setFieldUpdates(prev => prev.map((f, i) => i === idx ? { ...f, value: e.target.value } : f))}
                                        className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-violet-400" />
                                ) : (
                                    <input type="text" value={fu.value} placeholder={`New ${col?.label || fu.colKey} value…`}
                                        onChange={e => setFieldUpdates(prev => prev.map((f, i) => i === idx ? { ...f, value: e.target.value } : f))}
                                        className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-violet-400" />
                                )}
                                <button onClick={() => setFieldUpdates(prev => prev.filter((_, i) => i !== idx))}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        );
                    })}
                    {/* Add field picker */}
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-slate-800">
                        <select value={addKey} onChange={e => setAddKey(e.target.value)}
                            className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-dashed border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-violet-400">
                            <option value="">+ Add a field to update…</option>
                            {allFields.filter(c => !fieldUpdates.find(f => f.colKey === c.key)).map(c => (
                                <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                        </select>
                        <button onClick={addField} disabled={!addKey}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 disabled:pointer-events-none transition-all">
                            Add
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/60">
                    <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 border border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                        Cancel
                    </button>
                    <div className="flex-1" />
                    <button onClick={handleApply} disabled={applying || !fieldUpdates.length}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-extrabold text-white
                            bg-gradient-to-br from-violet-600 to-indigo-700
                            shadow-[0_2px_8px_rgba(99,102,241,0.3)]
                            hover:shadow-[0_4px_16px_rgba(99,102,241,0.4)]
                            hover:-translate-y-px active:translate-y-0 transition-all
                            disabled:opacity-50 disabled:pointer-events-none">
                        {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        Apply to {selectedCount} record{selectedCount !== 1 ? 's' : ''}
                    </button>
                </div>
            </div>
        </div>
    );
}
